import {
  SidebarList,
  SidebarListItemProps,
} from "@/lib/Components/SideBarList/SideBarList";
import { useState } from "react";
import { contactTypeColorMap } from "@/app/Contacts/Utils/contactTypeColorMap";
import {
  ContactListItem,
  contactListQuery,
} from "@/app/Contacts/GraphQL/contactListQuery";
import { Badge } from "@/components/catalyst/badge";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { getContactListQueryVariables } from "@/app/Contacts/Utils/getContactListQueryVariables";
import { keepPreviousData } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { ContactIcon } from "@/app/Contacts/Components/ContactIcon";
import { useTranslations } from "use-intl";
import { ContactType } from "@/gql/graphql";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

export function ContactListScreen() {
  const searchParams = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/contacts",
  });
  const { isAdmin } = useTenant();
  const [search, setSearch] = useState(searchParams.contactSearch ?? "");
  const defaultContactTypes = isAdmin
    ? [ContactType.Organisation, ContactType.User, ContactType.Guest]
    : [ContactType.Organisation, ContactType.Entity];
  const [selectedContactType, setSelectedContactType] =
    useState<ContactType[]>(defaultContactTypes);
  const t = useTranslations("contact");

  const availableContactTypes = isAdmin
    ? Object.values(ContactType)
    : [ContactType.Organisation, ContactType.Entity];

  const { data, isFetching, isFetchingNextPage, fetchNextPage } =
    useInfiniteGqlQuery(
      contactListQuery,
      getContactListQueryVariables({
        ...searchParams,
        contactSearch: search,
        contactType:
          selectedContactType.length > 0 ? selectedContactType : undefined,
      }),
      {
        placeholderData: keepPreviousData,
        initialPageParam: {
          page: 1,
        },
        getNextPageParam: (lastPage) => {
          return lastPage.contacts.paginatorInfo.hasMorePages
            ? {
                page: lastPage.contacts.paginatorInfo.currentPage + 1,
              }
            : undefined;
        },
      },
    );

  const items = data?.pages.flatMap((p) => p.contacts.data);
  const totalCount = data?.pages.at(0)?.contacts.paginatorInfo.total ?? 0;

  return (
    <SidebarList<ContactListItem>
      fetchNextPage={fetchNextPage}
      search={search}
      onSearchChange={setSearch}
      listRoute="/$tenant/$orgId/supplier/{-$supplierId}/contacts"
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      itemNode={(item) => <ContactNode model={item} />}
      items={items}
      totalCount={totalCount}
      linkProps={(item) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/contacts/$id",
        params: (prev) => ({
          ...prev,
          id: item.id,
        }),
      })}
      onClearFilters={() => {
        setSearch("");
        setSelectedContactType([]);
      }}
      filters={
        <MultiSelectFilter
          label="Type"
          options={availableContactTypes.map((type) => ({
            label: t(`type.${type}`),
            value: type,
          }))}
          selected={selectedContactType}
          onChange={setSelectedContactType}
        />
      }
    />
  );
}

function ContactNode({ model }: SidebarListItemProps<ContactListItem>) {
  const t = useTranslations("contact");

  return (
    <div className="flex items-start gap-3 py-2 text-xs">
      <div className="flex-shrink-0">
        <ContactIcon model={model} className="size-8" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{model.name}</p>
            <p className="truncate text-xs text-gray-500">
              {model.billing_email}
            </p>
          </div>
          <div className="flex-shrink-0">
            <Badge color={contactTypeColorMap[model.type]}>
              {t(`type.${model.type}`)}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}
